import { useEffect, useState } from 'react';
import { CalendarBlank, Users, ShieldCheck } from '@phosphor-icons/react';
import type { RoomOfferDTO } from '@pms/shared';
import { formatPrice } from '../../lib/format';

interface BookingWidgetProps {
  currency: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  rooms: RoomOfferDTO[];
  selectedUnitId: string | null;
  onChangeStay: (next: { checkIn: string; checkOut: string; guests: number }) => void;
  onReserve: () => void;
}

// Local calendar date (YYYY-MM-DD) — local, not toISOString() (UTC), which can
// read tomorrow after ~evening in the Americas and block today's check-in.
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Sticky booking widget: editable stay, price (cheapest-available → selected), Reserve. */
export function BookingWidget(props: BookingWidgetProps) {
  const [checkIn, setCheckIn] = useState(props.checkIn);
  const [checkOut, setCheckOut] = useState(props.checkOut);
  const [guests, setGuests] = useState(props.guests);

  useEffect(() => {
    setCheckIn(props.checkIn);
    setCheckOut(props.checkOut);
    setGuests(props.guests);
  }, [props.checkIn, props.checkOut, props.guests]);

  const hasDates = !!checkIn && !!checkOut && checkOut > checkIn;
  const dateInvalid = !!checkIn && !!checkOut && checkOut <= checkIn;

  const selected = props.rooms.find((r) => r.unitId === props.selectedUnitId) ?? null;
  const cheapest = props.rooms
    .filter((r) => r.available && r.fits && r.meetsMinStay)
    .sort((a, b) => (a.perStayTotal ?? a.nightlyFrom) - (b.perStayTotal ?? b.nightlyFrom))[0] ?? null;

  const priceRoom = selected ?? cheapest;
  const canReserve = hasDates && !!selected && selected.available && selected.fits && selected.meetsMinStay;
  const minCheckIn = today();

  function applyStay() {
    // Only propagate a complete, valid stay. Blurring the first date field
    // while the sibling is still empty would otherwise push a partial stay to
    // the parent (which drives our props); the props→local sync effect then
    // fires and clobbers the second date the guest is mid-entry. Waiting for
    // both dates keeps the round-trip idempotent so neither value is lost.
    if (!hasDates) return;
    props.onChangeStay({ checkIn, checkOut, guests });
  }

  return (
    <div className="rounded-[1.5rem] border border-sand bg-white p-6 shadow-[0_24px_50px_-36px_rgba(28,26,23,0.4)]">
      <p className="mb-4 font-serif text-lg font-bold text-ink">Reserve your stay</p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Check in" icon={<CalendarBlank className="h-4 w-4 text-clay" />}>
          <input type="date" value={checkIn} min={minCheckIn} onChange={(e) => setCheckIn(e.target.value)} onBlur={applyStay}
            aria-label="Check-in date" className="w-full bg-transparent text-sm text-ink outline-none" />
        </Field>
        <Field label="Check out" icon={<CalendarBlank className="h-4 w-4 text-clay" />}>
          <input type="date" value={checkOut} min={checkIn || minCheckIn} onChange={(e) => setCheckOut(e.target.value)} onBlur={applyStay}
            aria-label="Check-out date" className="w-full bg-transparent text-sm text-ink outline-none" />
        </Field>
        <Field label="Guests" icon={<Users className="h-4 w-4 text-clay" />}>
          <input type="number" min={1} value={guests} onChange={(e) => setGuests(Number(e.target.value))} onBlur={applyStay}
            aria-label="Guests" className="w-full bg-transparent text-sm text-ink outline-none" />
        </Field>
      </div>
      {dateInvalid && <p role="alert" className="mt-2 text-xs font-medium text-red-600">Check-out must be after check-in.</p>}

      <div className="mt-5 border-t border-sand pt-5" aria-live="polite">
        {!hasDates ? (
          <p className="text-sm text-ink-muted">Select dates to see your total.</p>
        ) : priceRoom ? (
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted">{selected ? 'Selected room' : 'From'}</p>
              <p className="font-serif text-3xl font-black text-ink">
                {priceRoom.perStayTotal != null
                  ? formatPrice(priceRoom.perStayTotal, props.currency)
                  : formatPrice(priceRoom.nightlyFrom, props.currency)}
              </p>
            </div>
            {priceRoom.nights != null && <p className="text-xs text-ink-muted">{priceRoom.nights} nights</p>}
          </div>
        ) : (
          <p className="text-sm text-ink-muted">No rooms available for these dates.</p>
        )}
      </div>

      <button
        type="button"
        disabled={!canReserve}
        onClick={props.onReserve}
        className="mt-5 w-full rounded-full bg-clay px-6 py-3.5 font-semibold text-white transition hover:bg-clay-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {selected ? 'Reserve' : hasDates ? 'Select a room' : 'Select dates'}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-ink-muted">
        <ShieldCheck className="h-3.5 w-3.5 text-sage" /> Booked directly with the hotel · free cancellation on most stays
      </p>
    </div>
  );
}

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="col-span-1 flex items-center gap-2 rounded-xl border border-sand px-2.5 py-2 last:col-span-2">
      {icon}
      <span className="flex min-w-0 flex-col">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
        {children}
      </span>
    </label>
  );
}
